import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GOOGLE_FORM_URL } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPERIENCE = [
  "Beginner (0-1 years)",
  "Intermediate (1-3 years)",
  "Advanced (3+ years)",
  "Returning after a break",
];

export const ApplicationForm = ({ innerRef }) => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    goals: "",
    experience: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.goals || !form.experience) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
      toast.success("Application received! Amani will be in touch soon.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          ? "Please check your details and try again."
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#121212] border border-white/15 text-white placeholder:text-neutral-500 px-4 py-3.5 focus:outline-none focus:border-[#E63946] transition-colors";

  return (
    <section
      id="apply"
      ref={innerRef}
      data-testid="apply-section"
      className="py-24 md:py-32 bg-[#0A0A0A] scroll-mt-20"
    >
      <div className="max-w-2xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#E63946]" />
            <span className="text-xs uppercase tracking-[0.3em] text-neutral-400">
              Apply
            </span>
            <span className="h-px w-10 bg-[#E63946]" />
          </div>
          <h2 className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">
            Apply for Coaching
          </h2>
          <p className="mt-4 text-neutral-300 leading-relaxed">
            Spots are limited. Tell me about your goals and I'll reach out to see
            if we're a fit.
          </p>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="apply-success"
            className="bg-[#171717] border border-white/10 p-10 text-center"
          >
            <CheckCircle2 size={48} className="text-[#E63946] mx-auto" />
            <h3 className="mt-5 font-heading text-2xl font-bold uppercase">
              Application Received
            </h3>
            <p className="mt-3 text-neutral-300">
              Thanks for applying. Amani will review your goals and get back to
              you shortly.
            </p>
            <a
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="apply-google-form-link"
              className="mt-6 inline-block text-sm uppercase tracking-widest text-[#E63946] hover:underline"
            >
              Or complete the full intake form →
            </a>
          </motion.div>
        ) : (
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="application-form"
            className="bg-[#171717] border border-white/10 p-8 sm:p-10 space-y-6"
          >
            <div>
              <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">
                Full Name
              </label>
              <input
                type="text"
                data-testid="lead-form-name-input"
                value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)}
                placeholder="John Carter"
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">
                Email Address
              </label>
              <input
                type="email"
                data-testid="lead-form-email-input"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@email.com"
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">
                Training Experience
              </label>
              <Select
                value={form.experience}
                onValueChange={(v) => update("experience", v)}
              >
                <SelectTrigger
                  data-testid="lead-form-experience-select"
                  className="w-full bg-[#121212] border-white/15 text-white px-4 py-6 rounded-none focus:border-[#E63946] focus:ring-0"
                >
                  <SelectValue placeholder="Select your level" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-white/15 text-white">
                  {EXPERIENCE.map((x) => (
                    <SelectItem key={x} value={x} data-testid={`experience-option-${x}`}>
                      {x}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">
                Your Goals
              </label>
              <textarea
                rows={4}
                data-testid="lead-form-goals-input"
                value={form.goals}
                onChange={(e) => update("goals", e.target.value)}
                placeholder="What plateau are you trying to break? What's your goal physique?"
                className={`${inputCls} resize-none`}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="lead-form-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#D90429] disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wide text-base px-8 py-4 transition-all hover:scale-[1.01]"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Submitting...
                </>
              ) : (
                "Submit Application"
              )}
            </button>

            <p className="text-center text-xs text-neutral-500">
              Prefer the full form?{" "}
              <a
                href={GOOGLE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="form-google-fallback-link"
                className="text-[#E63946] hover:underline uppercase tracking-widest"
              >
                Open intake form
              </a>
            </p>
          </motion.form>
        )}
      </div>
    </section>
  );
};
